import { FC, useMemo, useState } from 'react';

import { formatPrice } from '@shared/utils/formatPrice';

import { OrderStatBrandRow, OrderStatKind, OrderStatResult } from './adminOrderStat.types';
import { TD_CLASS, TH_CLASS } from './AnalyticsShared';

type SortKey =
  | 'orderCount'
  | 'amount'
  | 'feventCount'
  | 'quantity'
  | 'canceledCount'
  | 'avgAmount'
  | 'perFevent';

interface Column {
  key: SortKey;
  label: string;
  title?: string;
  /** 답례품에만 있는 칸 */
  giftOnly?: boolean;
}

const COLUMNS: Column[] = [
  { key: 'orderCount', label: '주문', title: '유효 주문 건수 (전체취소 제외)' },
  { key: 'amount', label: '금액', title: '화환=결제액, 답례품=결제액 − 취소액' },
  { key: 'avgAmount', label: '건당', title: '금액 ÷ 주문' },
  { key: 'quantity', label: '수량', title: '답례품은 취소수량을 뺀 순수량' },
  { key: 'feventCount', label: '상가', title: '주문이 붙은 상가 수' },
  { key: 'perFevent', label: '상가당', title: '주문 ÷ 상가' },
  {
    key: 'canceledCount',
    label: '전체취소',
    title: '결제 전액이 취소된 주문 수',
    giftOnly: true,
  },
];

const avgAmountOf = (row: OrderStatBrandRow) =>
  row.orderCount > 0 ? row.amount / row.orderCount : -1;
const perFeventOf = (row: OrderStatBrandRow) =>
  row.feventCount > 0 ? row.orderCount / row.feventCount : -1;

const sortValueOf = (row: OrderStatBrandRow, key: SortKey): number => {
  switch (key) {
    case 'avgAmount':
      return avgAmountOf(row);
    case 'perFevent':
      return perFeventOf(row);
    default:
      return row[key];
  }
};

const cellOf = (row: OrderStatBrandRow, key: SortKey): string => {
  switch (key) {
    case 'amount':
      return formatPrice(Math.round(row.amount));
    case 'avgAmount': {
      const value = avgAmountOf(row);
      return value < 0 ? '-' : formatPrice(Math.round(value));
    }
    case 'perFevent': {
      const value = perFeventOf(row);
      return value < 0 ? '-' : value.toFixed(1);
    }
    default:
      return row[key].toLocaleString();
  }
};

export const AdminOrderBrandTable: FC<{
  kind: OrderStatKind;
  result: OrderStatResult;
  onExport: () => void;
  onBrandClick: (brandKey: string, brandName: string) => void;
}> = function AdminOrderBrandTable({ kind, result, onExport, onBrandClick }) {
  const [sortKey, setSortKey] = useState<SortKey>('amount');
  const [desc, setDesc] = useState(true);

  const columns = useMemo(
    () => COLUMNS.filter((column) => kind === 'gift' || !column.giftOnly),
    [kind],
  );

  const rows = useMemo(() => {
    const sorted = [...result.brands];
    sorted.sort((a, b) => {
      const diff = sortValueOf(a, sortKey) - sortValueOf(b, sortKey);
      return desc ? -diff : diff;
    });
    return sorted;
  }, [result.brands, sortKey, desc]);

  const onHeaderClick = (key: SortKey) => {
    if (key === sortKey) setDesc((prev) => !prev);
    else {
      setSortKey(key);
      setDesc(true);
    }
  };

  const totalsRow: OrderStatBrandRow = { ...result.totals, key: '', brandName: '' };

  return (
    <div className="rounded-lg bg-white p-3 shadow-smd">
      <div className="flex items-center justify-between">
        <p className="text-16 font-bold text-gray-900">
          상조별 <span className="font-normal text-gray-500">({rows.length}곳)</span>
          <span className="ml-2 text-12 font-normal text-gray-500">
            행을 누르면 주문 목록
          </span>
        </p>
        <button
          type="button"
          onClick={onExport}
          className="shrink-0 rounded-lg bg-gray-800 px-3 py-1.5 text-13 font-medium text-white hover:bg-gray-700"
        >
          엑셀
        </button>
      </div>

      <div className="mt-2 overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead className="border-b border-gray-200">
            <tr>
              <th className={`${TH_CLASS} sticky left-0 bg-white text-left`} scope="col">
                상조
              </th>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={`${TH_CLASS} cursor-pointer select-none hover:text-gray-900`}
                  onClick={() => onHeaderClick(column.key)}
                  title={column.title}
                >
                  {column.label}
                  {sortKey === column.key && (desc ? ' ▾' : ' ▴')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row) => (
              <tr
                key={row.key}
                className="cursor-pointer hover:bg-indigo-50"
                onClick={() => onBrandClick(row.key, row.brandName)}
                title={`${row.brandName} 주문 목록 보기`}
              >
                <td
                  className={`${TD_CLASS} sticky left-0 bg-white text-left font-semibold text-indigo-700 underline-offset-2 hover:underline`}
                >
                  {row.brandName}
                </td>
                {columns.map((column) => (
                  <td key={column.key} className={TD_CLASS}>
                    {cellOf(row, column.key)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-gray-200">
            <tr className="bg-gray-50 font-semibold">
              <td className={`${TD_CLASS} sticky left-0 bg-gray-50 text-left`}>합계</td>
              {columns.map((column) => (
                <td key={column.key} className={TD_CLASS}>
                  {cellOf(totalsRow, column.key)}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};
